import React from 'react';
import { X, Clock, User, Users, Calendar, MessageSquare, Star, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Meeting } from '@clareefai/types';

interface MeetingDetailsModalProps {
  meeting: Meeting | null;
  isOpen: boolean;
  onClose: () => void;
}

const MeetingDetailsModal: React.FC<MeetingDetailsModalProps> = ({ meeting, isOpen, onClose }) => {
  const { t, i18n } = useTranslation();
  if (!isOpen || !meeting) return null;

  const formatDateTime = (dateTimeStr: string | null) => {
    if (!dateTimeStr) return t('shared.meetingDetails.na');
    try {
      const date = new Date(dateTimeStr);
      // Check if date is valid
      if (isNaN(date.getTime())) return t('shared.meetingDetails.na');
      return date.toLocaleString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      });
    } catch (e) {
      return t('shared.meetingDetails.na');
    }
  };

  const formatDuration = (minutes: number | null) => {
    if (minutes === null || minutes === undefined) return t('shared.meetingDetails.na');
    // Ensure non-negative duration
    const absMinutes = Math.abs(minutes);
    const hours = Math.floor(absMinutes / 60);
    const mins = absMinutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    // For very short meetings (< 1 minute), show seconds if available
    if (absMinutes === 0 && meeting.meeting_started_at && meeting.meeting_ended_at) {
      const start = new Date(meeting.meeting_started_at);
      const end = new Date(meeting.meeting_ended_at);
      const seconds = Math.floor((end.getTime() - start.getTime()) / 1000);
      if (seconds > 0) {
        return `${seconds}s`;
      }
    }
    return `${mins}m`;
  };

  const isCompleted = meeting.status === 'completed';

  return (
    <div className="fixed inset-0 z-[2000] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-2xl font-bold text-gray-900">{t('shared.meetingDetails.heading')}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Meeting Overview */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {meeting.type.replace('_', ' ')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {meeting.date && meeting.time
                      ? `${new Date(meeting.date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                        })} ${t('shared.meetingDetails.atTime')} ${meeting.time}`
                      : t('shared.meetingDetails.dateNotSet')}
                  </p>
                </div>
                <span
                  className={`status-badge ${
                    meeting.status === 'request'
                      ? 'status-invited'
                      : meeting.status === 'progress'
                      ? 'status-review'
                      : meeting.status === 'booked'
                      ? 'status-available'
                      : meeting.status === 'completed'
                      ? 'status-completed'
                      : 'status-failed'
                  }`}
                >
                  {meeting.status === 'request'
                    ? t('shared.meetingDetails.statusRequest')
                    : meeting.status === 'progress'
                    ? t('shared.meetingDetails.statusProgress')
                    : meeting.status === 'booked'
                    ? t('shared.meetingDetails.statusConfirmed')
                    : meeting.status === 'completed'
                    ? t('shared.meetingDetails.statusCompleted')
                    : t('shared.meetingDetails.statusToReschedule')}
                </span>
              </div>

              {meeting.timezone && (
                <p className="text-sm text-gray-500">{t('shared.meetingDetails.timezoneLabel', { timezone: meeting.timezone })}</p>
              )}
            </div>

            {/* Meeting Participants Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Promoter Info */}
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center space-x-2 mb-3">
                  <Users className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-green-900">{t('shared.meetingDetails.promoter')}</h4>
                </div>
                <div className="space-y-2">
                  <p className="font-medium text-gray-900">{meeting.promoter_name || t('shared.meetingDetails.unknown')}</p>
                  {meeting.promoter_company && (
                    <p className="text-sm text-gray-600">{meeting.promoter_company}</p>
                  )}
                  {meeting.promoter_email && (
                    <p className="text-sm text-gray-600">{meeting.promoter_email}</p>
                  )}
                  {isCompleted && meeting.promoter_joined_at && (
                    <div className="mt-3 pt-3 border-t border-green-200">
                      <p className="text-xs text-gray-500 mb-1">{t('shared.meetingDetails.joinedAt')}</p>
                      <p className="text-sm text-gray-700 flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {formatDateTime(meeting.promoter_joined_at)}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Prospect Info */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center space-x-2 mb-3">
                  <User className="w-5 h-5 text-blue-600" />
                  <h4 className="font-semibold text-blue-900">{t('shared.meetingDetails.prospect')}</h4>
                </div>
                <div className="space-y-2">
                  <p className="font-medium text-gray-900">{meeting.prospect_name || t('shared.meetingDetails.unknown')}</p>
                  {meeting.prospect_company && (
                    <p className="text-sm text-gray-600">{meeting.prospect_company}</p>
                  )}
                  {meeting.prospect_email && (
                    <p className="text-sm text-gray-600">{meeting.prospect_email}</p>
                  )}
                  {isCompleted && meeting.prospect_joined_at && (
                    <div className="mt-3 pt-3 border-t border-blue-200">
                      <p className="text-xs text-gray-500 mb-1">{t('shared.meetingDetails.joinedAt')}</p>
                      <p className="text-sm text-gray-700 flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {formatDateTime(meeting.prospect_joined_at)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Meeting Statistics (for completed meetings) */}
            {isCompleted && (
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <h4 className="font-semibold text-purple-900 mb-3 flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  {t('shared.meetingDetails.statsHeading')}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {meeting.meeting_started_at && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{t('shared.meetingDetails.started')}</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDateTime(meeting.meeting_started_at)}
                      </p>
                    </div>
                  )}
                  {meeting.meeting_ended_at && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{t('shared.meetingDetails.ended')}</p>
                      <p className="text-sm font-medium text-gray-900">
                        {formatDateTime(meeting.meeting_ended_at)}
                      </p>
                    </div>
                  )}
                  {meeting.duration_minutes !== null && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{t('shared.meetingDetails.duration')}</p>
                      <p className="text-sm font-medium text-gray-900 flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {formatDuration(meeting.duration_minutes)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Feedback and Rating */}
            {(meeting.outcome || meeting.rating) && (
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <h4 className="font-semibold text-yellow-900 mb-3 flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2" />
                  {t('shared.meetingDetails.feedback')}
                </h4>
                {meeting.rating && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">{t('shared.meetingDetails.ratingLabel')}</p>
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${
                            i < meeting.rating!
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      ))}
                      <span className="ml-2 text-sm text-gray-700">{meeting.rating}/5</span>
                    </div>
                  </div>
                )}
                {meeting.outcome && (
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t('shared.meetingDetails.outcomeNotes')}</p>
                    <p className="text-sm text-gray-700">{meeting.outcome}</p>
                  </div>
                )}
              </div>
            )}

            {/* Additional Notes */}
            {meeting.notes && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-2">{t('shared.meetingDetails.notes')}</h4>
                <p className="text-sm text-gray-700">{meeting.notes}</p>
              </div>
            )}

            {/* Date History */}
            {(meeting.requested_dates.length > 0 ||
              meeting.rescheduled_dates.length > 0 ||
              meeting.accepted_date) && (
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-3">{t('shared.meetingDetails.dateHistory')}</h4>
                <div className="space-y-2">
                  {meeting.accepted_date && (
                    <div className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-gray-600">{t('shared.meetingDetails.acceptedLabel')}</span>
                      <span className="font-medium text-gray-900">
                        {meeting.accepted_date.date} {t('shared.meetingDetails.atTime')} {meeting.accepted_date.time}
                      </span>
                    </div>
                  )}
                  {meeting.requested_dates.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{t('shared.meetingDetails.requestedDates')}</p>
                      <ul className="list-disc list-inside space-y-1">
                        {meeting.requested_dates.map((date) => (
                          <li key={date.id} className="text-sm text-gray-700">
                            {date.date} {t('shared.meetingDetails.atTime')} {date.time} ({date.status})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {meeting.rescheduled_dates.length > 0 && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{t('shared.meetingDetails.rescheduledDates')}</p>
                      <ul className="list-disc list-inside space-y-1">
                        {meeting.rescheduled_dates.map((date) => (
                          <li key={date.id} className="text-sm text-gray-700">
                            {date.date} {t('shared.meetingDetails.atTime')} {date.time} ({date.status})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingDetailsModal;
